<template>
	<div class="virtual-transfer-container">
		<!-- Excel模板操作按钮 -->
		<div class="template-operations">
			<el-upload
				:auto-upload="false"
				:show-file-list="false"
				accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
				action=""
				:on-change="handleImportTemplate">
				<el-button type="primary" icon="el-icon-upload">导入模板</el-button>
			</el-upload>

			<div style="width: 20px; display: inline-block"></div>
			<el-button type="primary" icon="el-icon-download" plain @click="handleExportTemplate">导出模板</el-button>
		</div>

		<!-- 原有的Transfer组件 -->
		<div class="virtual-transfer">
			<div class="virtual-transfer-panel left-panel">
				<div class="panel-header">
					<div class="panel-title">
						<el-checkbox :value="leftAllSelected" :indeterminate="leftIndeterminate" @input="handleLeftSelectAll">
							{{ titles[0] }}
						</el-checkbox>
					</div>
					<div class="panel-stats">{{ leftFilteredData.length }}/{{ unselectedData.length }}</div>
				</div>
				<div v-if="filterable" class="panel-filter">
					<el-input
						v-model="leftFilterText"
						:placeholder="`搜索${titles[0]}`"
						prefix-icon="el-icon-search"
						size="small"
						clearable
						@input="handleLeftFilter"></el-input>
				</div>
				<div class="panel-body">
					<virtual-list
						ref="leftVirtualList"
						:data-key="props.key"
						:data-sources="leftFilteredData"
						:data-component="itemComponent"
						:extra-props="{
							onItemClick: handleLeftItemClick,
							selectedKeys: leftSelectedKeys,
							props: props,
						}"
						:estimate-size="32"
						style="height: 400px; overflow-y: auto"></virtual-list>
				</div>
			</div>

			<div class="virtual-transfer-buttons">
				<el-button :disabled="leftSelectedItems.length === 0" size="small" type="primary" @click="moveToRight">
					{{ buttonTexts[1] || '到右边' }}
					<i class="el-icon-arrow-right"></i>
				</el-button>
				<el-button :disabled="rightSelectedItems.length === 0" size="small" type="primary" @click="moveToLeft">
					<i class="el-icon-arrow-left"></i>
					{{ buttonTexts[0] || '到左边' }}
				</el-button>
			</div>

			<div class="virtual-transfer-panel right-panel">
				<div class="panel-header">
					<div class="panel-title">
						<el-checkbox :value="rightAllSelected" :indeterminate="rightIndeterminate" @input="handleRightSelectAll">
							{{ titles[1] }}
						</el-checkbox>
					</div>
					<div class="panel-stats">{{ rightFilteredData.length }}/{{ selectedData.length }}</div>
				</div>
				<div v-if="filterable" class="panel-filter">
					<el-input
						v-model="rightFilterText"
						:placeholder="`搜索${titles[1]}`"
						prefix-icon="el-icon-search"
						size="small"
						clearable
						@input="handleRightFilter"></el-input>
				</div>
				<div class="panel-body">
					<virtual-list
						ref="rightVirtualList"
						:data-key="props.key"
						:data-sources="rightFilteredData"
						:data-component="itemComponent"
						:extra-props="{
							onItemClick: handleRightItemClick,
							selectedKeys: rightSelectedKeys,
							props: props,
						}"
						:estimate-size="32"
						style="height: 400px; overflow-y: auto"></virtual-list>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import VirtualList from 'vue-virtual-scroll-list'
import _ from 'lodash'
import {downloadLocalFile} from '@/util/util'

/**
 * 虚拟滚动Transfer组件的列表项组件
 */
const TransferItem = {
	name: 'TransferItem',
	props: {
		index: {
			type: Number,
			required: true,
		},
		source: {
			type: Object,
			required: true,
		},
		onItemClick: {
			type: Function,
			required: true,
		},
		selectedKeys: {
			type: Array,
			default: () => [],
		},
		props: {
			type: Object,
			default: () => ({}),
		},
	},
	computed: {
		/**
		 * 是否选中状态
		 * @returns {boolean}
		 */
		isSelected() {
			const {key} = this.props
			const itemKey = this.source[key]
			return this.selectedKeys.includes(itemKey)
		},

		/**
		 * 显示标签
		 * @returns {string}
		 */
		label() {
			const {label} = this.props
			return this.source[label] || ''
		},
	},
	methods: {
		/**
		 * 处理项目点击事件
		 */
		handleClick() {
			this.onItemClick(this.source)
		},
	},
	render(h) {
		return h(
			'div',
			{
				class: {
					'transfer-item': true,
					'is-selected': this.isSelected,
				},
				style: {
					height: '32px',
					lineHeight: '32px',
					padding: '0 15px',
					cursor: 'pointer',
					display: 'flex',
					alignItems: 'center',
					boxSizing: 'border-box',
				},
				on: {
					click: this.handleClick,
				},
			},
			[
				h(
					'el-checkbox',
					{
						props: {
							value: this.isSelected,
						},
						on: {
							input: (e) => {
								// 阻止checkbox自身的状态改变
								// 所有选择逻辑由外层div的click事件统一处理
								this.handleClick(!e)
							},
						},
						style: {
							width: '100%',
							pointerEvents: 'none',
						},
					},
					[
						h(
							'span',
							{
								slot: 'default',
								style: {
									pointerEvents: 'none',
								},
							},
							this.label,
						),
					],
				),
			],
		)
	},
}

export default {
	name: 'VirtualTransfer',
	components: {
		VirtualList,
	},
	props: {
		/**
		 * 双栏数据源
		 */
		data: {
			type: Array,
			default: () => [],
		},
		/**
		 * 已选中的数据keys
		 */
		value: {
			type: Array,
			default: () => [],
		},
		/**
		 * 数据配置项
		 */
		props: {
			type: Object,
			default: () => ({
				key: 'id',
				label: 'name',
			}),
		},
		/**
		 * 双栏标题
		 */
		titles: {
			type: Array,
			default: () => ['可选项', '已选项'],
		},
		/**
		 * 按钮文字
		 */
		buttonTexts: {
			type: Array,
			default: () => ['到左边', '到右边'],
		},
		/**
		 * 是否可搜索
		 */
		filterable: {
			type: Boolean,
			default: false,
		},
		/**
		 * 完整的可选数据列表（用于Excel导入匹配）
		 */
		canSelectList: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			// 左侧搜索文本
			leftFilterText: '',
			// 右侧搜索文本
			rightFilterText: '',
			// 左侧选中项
			leftSelectedItems: [],
			// 右侧选中项
			rightSelectedItems: [],
			// 列表项组件
			itemComponent: TransferItem,
		}
	},
	computed: {
		/**
		 * 已选中的数据
		 * @returns {Array}
		 */
		selectedData() {
			const {key} = this.props
			return this.data.filter((item) => this.value.includes(item[key]))
		},

		/**
		 * 未选中的数据
		 * @returns {Array}
		 */
		unselectedData() {
			const {key} = this.props
			return this.data.filter((item) => !this.value.includes(item[key]))
		},

		/**
		 * 左侧过滤后的数据
		 * @returns {Array}
		 */
		leftFilteredData() {
			return this.filterData(this.unselectedData, this.leftFilterText)
		},

		/**
		 * 右侧过滤后的数据
		 * @returns {Array}
		 */
		rightFilteredData() {
			return this.filterData(this.selectedData, this.rightFilterText)
		},

		/**
		 * 左侧选中的keys
		 * @returns {Array}
		 */
		leftSelectedKeys() {
			const {key} = this.props
			return this.leftSelectedItems.map((item) => item[key])
		},

		/**
		 * 右侧选中的keys
		 * @returns {Array}
		 */
		rightSelectedKeys() {
			const {key} = this.props
			return this.rightSelectedItems.map((item) => item[key])
		},

		/**
		 * 左侧全选状态
		 * @returns {boolean}
		 */
		leftAllSelected() {
			return this.leftFilteredData.length > 0 && this.leftSelectedItems.length === this.leftFilteredData.length
		},

		/**
		 * 左侧半选状态
		 * @returns {boolean}
		 */
		leftIndeterminate() {
			return this.leftSelectedItems.length > 0 && this.leftSelectedItems.length < this.leftFilteredData.length
		},

		/**
		 * 右侧全选状态
		 * @returns {boolean}
		 */
		rightAllSelected() {
			return this.rightFilteredData.length > 0 && this.rightSelectedItems.length === this.rightFilteredData.length
		},

		/**
		 * 右侧半选状态
		 * @returns {boolean}
		 */
		rightIndeterminate() {
			return this.rightSelectedItems.length > 0 && this.rightSelectedItems.length < this.rightFilteredData.length
		},
	},
	watch: {
		value: {
			handler() {
				// 清空选中状态
				this.leftSelectedItems = []
				this.rightSelectedItems = []
			},
			immediate: true,
		},
	},
	created() {
		// 创建防抖的过滤方法
		this.debouncedLeftFilter = _.debounce(() => {
			// 左侧过滤处理
		}, 300)
		this.debouncedRightFilter = _.debounce(() => {
			// 右侧过滤处理
		}, 300)
	},
	methods: {
		/**
		 * 过滤数据
		 * @param {Array} data - 原始数据
		 * @param {string} filterText - 过滤文本
		 * @returns {Array} 过滤后的数据
		 */
		filterData(data, filterText) {
			if (!filterText) return data

			const {label} = this.props
			return data.filter((item) => {
				const itemLabel = item[label] || ''
				return itemLabel.toLowerCase().includes(filterText.toLowerCase())
			})
		},

		/**
		 * 处理左侧搜索过滤
		 */
		handleLeftFilter() {
			this.debouncedLeftFilter()
		},

		/**
		 * 处理右侧搜索过滤
		 */
		handleRightFilter() {
			this.debouncedRightFilter()
		},

		/**
		 * 处理左侧项目点击
		 * @param {Object} item - 点击的项目
		 */
		handleLeftItemClick(item) {
			const {key} = this.props
			const itemKey = item[key]
			const index = this.leftSelectedItems.findIndex((selected) => selected[key] === itemKey)

			if (index > -1) {
				this.leftSelectedItems.splice(index, 1)
			} else {
				this.leftSelectedItems.push(item)
			}
		},

		/**
		 * 处理右侧项目点击
		 * @param {Object} item - 点击的项目
		 */
		handleRightItemClick(item) {
			const {key} = this.props
			const itemKey = item[key]
			const index = this.rightSelectedItems.findIndex((selected) => selected[key] === itemKey)

			if (index > -1) {
				this.rightSelectedItems.splice(index, 1)
			} else {
				this.rightSelectedItems.push(item)
			}
		},

		/**
		 * 移动到右侧
		 */
		moveToRight() {
			if (this.leftSelectedItems.length === 0) return

			const {key} = this.props
			const newSelectedKeys = [...this.value, ...this.leftSelectedItems.map((item) => item[key])]

			this.$emit('input', newSelectedKeys)
			this.$emit(
				'change',
				newSelectedKeys,
				'right',
				this.leftSelectedItems.map((item) => item[key]),
			)

			this.leftSelectedItems = []
		},

		/**
		 * 移动到左侧
		 */
		moveToLeft() {
			if (this.rightSelectedItems.length === 0) return

			const {key} = this.props
			const removeKeys = this.rightSelectedItems.map((item) => item[key])
			const newSelectedKeys = this.value.filter((k) => !removeKeys.includes(k))

			this.$emit('input', newSelectedKeys)
			this.$emit('change', newSelectedKeys, 'left', removeKeys)

			this.rightSelectedItems = []
		},

		/**
		 * 处理左侧全选
		 */
		handleLeftSelectAll() {
			if (this.leftAllSelected) {
				// 如果当前是全选状态，则取消全选
				this.leftSelectedItems = []
			} else {
				// 否则全选当前过滤后的数据
				this.leftSelectedItems = [...this.leftFilteredData]
			}
		},

		/**
		 * 处理右侧全选
		 */
		handleRightSelectAll() {
			if (this.rightAllSelected) {
				// 如果当前是全选状态，则取消全选
				this.rightSelectedItems = []
			} else {
				// 否则全选当前过滤后的数据
				this.rightSelectedItems = [...this.rightFilteredData]
			}
		},

		/**
		 * 处理导出模板
		 */
		handleExportTemplate() {
			//导出本地模板 奖励结算费率渠道模板.xlsx
			downloadLocalFile('奖励结算费率渠道模板')
		},

		/**
		 * 处理导入模板
		 * @param {Object} file - 上传的文件对象
		 */
		async handleImportTemplate(file) {
			try {
				const data = await this.$Export.xlsx(file.raw)
				let {results = []} = data || {}
				if (results.length <= 0) {
					this.$message.error('批量导入文件数据为空！')
					return
				}
				this.matchChannelData(results)
			} catch (error) {
				console.error('导入失败', error)
				this.$message.error('导入失败')
			}
		},

		/**
		 * 匹配渠道数据
		 * @param {Array} channelNames - 从Excel导入的渠道名称数组
		 */
		matchChannelData(channelNames) {
			const {label} = this.props
			const matchedChannels = []
			const unmatchedNames = []

			// 遍历导入的渠道名称，在canSelectList中查找匹配项
			channelNames.forEach((channelItemData) => {
				let {渠道名称: name = ''} = channelItemData || {}
				if (!name || typeof name !== 'string') return
				const found = this.canSelectList.find((item) => {
					const itemName = item[label] || ''
					let itemNameTrim = itemName.trim()
					let nameTrim = name.trim()
					return itemNameTrim === nameTrim || itemNameTrim.includes(nameTrim)
				})

				if (found) {
					matchedChannels.push(found)
				} else {
					unmatchedNames.push(name)
				}
			})

			// 更新组件数据
			if (matchedChannels.length > 0) {
				// 发出事件通知父组件更新数据
				this.$emit('import-channels', matchedChannels)
				// 显示导入结果
				let message = `成功导入${matchedChannels.length}个渠道`
				if (unmatchedNames.length > 0) {
					message += `，${unmatchedNames.length}个渠道名称未匹配成功`
					console.warn('未匹配的渠道名称:', unmatchedNames)
				}
				this.$message.success(message)
				// 如果有未匹配的渠道，显示详细信息
				if (unmatchedNames.length > 0) {
					this.$alert(`以下渠道名称在系统中未找到：${unmatchedNames.join('、')}`, '提示')
				}
			} else {
				this.$message.error('没有匹配到任何渠道，请检查渠道名称是否正确')
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.virtual-transfer-container {
	.template-operations {
		margin-bottom: 20px;
		padding: 15px;
		background: #f8f9fa;
		border: 1px solid #e9ecef;
		border-radius: 4px;
		display: flex;
		align-items: center;

		.el-button {
			margin-right: 0;
		}

		::v-deep .el-upload {
			display: inline-block;
		}
	}
}

.virtual-transfer {
	display: flex;
	align-items: flex-start;
	gap: 20px;

	.virtual-transfer-panel {
		width: 350px;
		border: 1px solid #ebeef5;
		border-radius: 4px;
		overflow: hidden;

		.panel-header {
			background: #f5f7fa;
			padding: 10px 15px;
			display: flex;
			justify-content: space-between;
			align-items: center;
			border-bottom: 1px solid #ebeef5;

			.panel-title {
				font-weight: 500;
				color: #303133;
				display: flex;
				align-items: center;

				.el-checkbox {
					.el-checkbox__label {
						font-weight: 500;
						color: #303133;
					}
				}
			}

			.panel-stats {
				color: #909399;
				font-size: 12px;
			}
		}

		.panel-filter {
			padding: 10px;
			border-bottom: 1px solid #ebeef5;
		}

		.panel-body {
			height: 400px;
			overflow: hidden;
			position: relative;
		}
	}

	.virtual-transfer-buttons {
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: 10px;
	}
}

::v-deep .transfer-item {
	transition: background-color 0.3s;

	&:hover {
		background-color: #f5f7fa !important;
	}

	&.is-selected {
		background-color: #ecf5ff !important;
		color: #409eff;
	}

	.el-checkbox {
		display: flex;
		align-items: center;
		.el-checkbox__input {
			pointer-events: auto;
		}

		.el-checkbox__label {
			width: calc(100% - 20px);
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			padding-left: 8px;
		}
	}
}

::v-deep .el-button + .el-button,
.el-checkbox.is-bordered + .el-checkbox.is-bordered {
	margin-left: 0;
}
</style>
